import { CreativeType } from 'core/creative/Creative';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { CampaignLimitation } from 'core/limitation/Limitation';
import { OutboundInfo } from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useKeywordForm';
import { Product } from 'core/product/Product';

export type RtbCampaign = {
  keywords?: any;
  basic: RtbCampaignBasic;
  limitations: CampaignLimitation;
  products?: Product[];
  autoProducts?: Product[];
  creatives?: any[];
};
/**
 *
 * @param id 活動ID
 * @param name 活動名稱
 * @param groupId 活動群組ID
 * @param advertiserId 廣告主ID
 * @param orderId 訂單ID
 * @param budget 預算
 * @param startDate 開始日期
 * @param endDate 結束日期
 * @param priceModel 價格模式
 * @param optimize 優化目標
 * @param creativeDeliverType 創意投放模式
 * @param deliverType 投放模式
 * @param effectiveStatus 生效狀態
 * @param spents 花費
 * @param currencyRate 匯率
 * @param olapActualSpent OLAP實際花費
 * @param olapExpectSpent OLAP預期花費
 * @param expectedSpent 預期花費
 * @param impres 曝光
 * @param clicks 點擊
 * @param dailyTargetBudget 每日預算
 * @param frequency 頻率
 * @param dayPart 時段
 * @param checkpoints 檢查點
 * @param dealIds 合約ID
 * @param adType 活動類型
 * @param additionalInfo 額外資訊
 * @param bindings 綁定
 * @param smartCampaignConfigId 智能投放配置ID
 * @param report 報表
 * @param ageMin 年齡下限
 * @param ageMax 年齡上限
 * @param webBrandNo 黃金置頂 品牌編號
 * @param selectPattern 智能投放3.0 選品模式
 * @param budgetType 預算類型(日預算or總預算)
 * @param runningPattern 智能投放3.0 走期模式
 * @param roasStrategy 智能投放3.0 ROAS策略
 */
export type RtbCampaignBasic = {
  id?: number;
  name: string;
  groupId: number;
  advertiserId: number;
  orderId: number;
  budget: number;
  startDate: string;
  endDate: string;
  createDate?: string;
  priceModel: RtbCampaignPlanType;
  orderPrice?: number;
  optimize: L2ObjectOptimizationGoal;
  bidPrice?: number;
  creativeDeliverType: CreativeDeliverType;
  deliverType: DeliverType;
  state?: CampaignState;
  effectiveStatus?: string;
  spents?: number;
  currencyRate?: number;
  olapActualSpent?: number;
  olapExpectSpent?: number;
  expectedSpent: number;
  impres?: number;
  clicks?: number;
  dailyTargetBudget: number | null;
  frequency?: CampaignFrequency;
  dayPart?: { [key: string]: string[] | number[] | string };
  checkpoints: CheckPoint[];
  dealIds?: string[];
  adType: AdType;
  additionalInfo?: AdditionalInfo;
  bindings?: {
    bindingId: string;
    active: boolean;
  };
  smartCampaignConfigId?: string;
  report?: any;
  ageMin: number;
  ageMax: number;
  webBrandNo?: string;
  brandName?: string;
  selectPattern?: string | null;
  budgetType?: string | null;
  runningPattern?: string | null;
  bidMode?: string | null;
  roasOption?: {
    roasStrategy?: string;
    roas?: number | null;
  };
  roas?: number;
  outboundInfo?: OutboundInfo;
  enableOutbound?: boolean;
  category?: {
    value: string;
    label: string;
  }[];
};

export type AdditionalInfo = {
  creativeAmount: CreativeAmount;
  limitations: { [key: string]: object[] };
};

export type CreativeAmount = {
  bindingCount: number;
  enableCount: number;
};

export type CampaignFrequency = {
  maxFrequency: number;
  intervalDays: number;
};

export type CheckPoint = {
  target: number;
  time: string;
  utime: string;
};
/**
 *
 * @param DISPLAY 展示廣告
 * @param KEYWORD 手動關鍵字廣告
 * @param SMART_KEYWORD 智能關鍵字廣告
 * @param MAX_CVR_KEYWORD 關鍵字廣告
 * @param SMART_CONFIG 智能配置廣告
 * @param UNKNOWN 未知
 * @param SHOP 黃金置頂搜尋廣告
 * @param RECOMMEND 推薦廣告
 * @param OUTBOUND_KEYWORD 聯播網廣告
 */
export enum AdType {
  DISPLAY = 'DISPLAY',
  KEYWORD = 'KEYWORD',
  SMART_KEYWORD = 'SMART_KEYWORD',
  MAX_CVR_KEYWORD = 'MAX_CVR_KEYWORD',
  SMART_CONFIG = 'SMART_CONFIG',
  UNKNOWN = 'UNKNOWN',
  SHOP = 'SHOP',
  RECOMMEND = 'RECOMMEND',
  OUTBOUND_KEYWORD = 'OUTBOUND_KEYWORD',
}

export enum RtbOptimize {
  IMPRESSIONS = 'IMPRESSIONS',
  CLICKS = 'CLICKS',
}

export enum RtbCampaignPlanType {
  RS = 'rs',
  FCPC = 'fcpc',
  FCPM = 'fcpm',
  RS_CPC = 'rsCpc',
  RS_CPM = 'rsCpm',
}

export enum CreativeDeliverType {
  OPTIMIZE = 1,
  AVERAGE = 2,
}

export enum CampaignState {
  ACTIVATE = 1,
  DEACTIVATE = 2,
  DELETE = 3,
  STOPPING = 4,
}

export enum DailyBudgetPlan {
  SCHEDULE,
  DAILY,
}

export enum DeliverType {
  STANDARD = 1,
  ACCELERATED = 2,
}

export const AD_TYPE_MAP_CREATIVE_TYPE = {
  [AdType.DISPLAY]: [
    CreativeType.IMAGE,
    CreativeType.PRODUCT_NATIVE,
    CreativeType.RICH_PRODUCT_NATIVE,
  ],
  [AdType.KEYWORD]: [CreativeType.PRODUCT_NATIVE],
  [AdType.SMART_KEYWORD]: [CreativeType.PRODUCT_NATIVE],
  [AdType.MAX_CVR_KEYWORD]: [CreativeType.PRODUCT_NATIVE],
  [AdType.SMART_CONFIG]: [CreativeType.PRODUCT_NATIVE], // TODO: If smart config allows other adType, should add corresponding creativeType here
  [AdType.SHOP]: [CreativeType.SHOP],
  [AdType.RECOMMEND]: [CreativeType.PRODUCT_NATIVE],
  [AdType.OUTBOUND_KEYWORD]: [CreativeType.PRODUCT_NATIVE],
};

export const RTBCAMPAIGN_DEFAULT_AGE_MIN = 13;
export const RTBCAMPAIGN_DEFAULT_AGE_MAX = 65;

export enum Gender {
  ALL = -1,
  MALE = 1,
  FEMALE,
}
